import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';
import { ThemeEvents, FilterUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {HTMLElement | null} */
  #loadMoreContainer = null;

  /** @type {HTMLButtonElement | null} */
  #loadMoreButton = null;

  /** @type {HTMLElement | null} */
  #progressText = null;

  /** @type {HTMLElement | null} */
  #progressBarFill = null;

  /** @type {string} */
  #loadMode = 'infinite';

  /** @type {number} */
  #totalArticles = 0;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {boolean} */
  #isLoadingNextPage = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#initializeLoadMode();
    this.#setupCategoryFilterTabs();
    
    requestIdleCallback(() => {
      this.#redistributeHiddenColumnElements();
    });
    
    if (this.#loadMode === 'infinite') {
      this.#overrideInfiniteScroll();
    }
  }

  #overrideInfiniteScroll() {
    if (!this.infinityScrollObserver) return;
    
    const originalObserver = this.infinityScrollObserver;
    const { viewMoreNext } = this.refs;
    
    if (!viewMoreNext) return;
    
    originalObserver.disconnect();
    originalObserver.unobserve(viewMoreNext);
    
    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target === viewMoreNext) {
            await this.#renderNextPageForInfinite();
          }
        }
      },
      { rootMargin: '100px' }
    );
    
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  async #renderNextPageForInfinite() {
    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    if (this.#isLoadingNextPage) return;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);
    if (!nextPageItemElements) {
      this.#isLoadingNextPage = true;
      
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      try {
        this.#fetchPage('next').catch((error) => {
          this.#resolveNextPagePromise?.();
          this.#resolveNextPagePromise = null;
        });
        await promise;
      } finally {
        this.#isLoadingNextPage = false;
      }
      
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    const pageHTML = this.pages.get(nextPage.page);
    if (pageHTML) {
      const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
      const newPageGrid = parsedPage.querySelector('[ref="grid"]');
      const columnsNew = newPageGrid?.querySelector('.blog-posts-columns');
      if (columnsNew) {
        const elementsFromColumns = Array.from(columnsNew.querySelectorAll('[ref="cards[]"]'))
          .filter(el => el instanceof HTMLElement);
        this.#addElementsToAllColumns(elementsFromColumns);
      } else {
        const elements = Array.from(nextPageItemElements)
          .filter(el => el instanceof HTMLElement);
        this.#addElementsToAllColumns(elements);
      }
    } else {
      const elements = Array.from(nextPageItemElements)
        .filter(el => el instanceof HTMLElement);
      this.#addElementsToAllColumns(elements);
    }

    this.updateHistoryState(nextPage.url);

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initializeLoadMode();
    
    if (this.#loadMode === 'button') {
      requestIdleCallback(() => {
        if (this.#loadMode === 'button' && this.infinityScrollObserver) {
          this.infinityScrollObserver.disconnect();
          const { viewMorePrevious, viewMoreNext } = this.refs;
          if (viewMorePrevious instanceof HTMLElement) {
            this.infinityScrollObserver.unobserve(viewMorePrevious);
          }
          if (viewMoreNext instanceof HTMLElement) {
            this.infinityScrollObserver.unobserve(viewMoreNext);
          }
          this.infinityScrollObserver = undefined;
        }
      });
    } else if (this.#loadMode === 'infinite') {
      requestIdleCallback(() => {
        this.#overrideInfiniteScroll();
      });
    }
  }

  #initializeLoadMode() {
    const { grid } = this.refs;
    
    if (grid && grid instanceof HTMLElement) {
      const loadModeValue = grid.dataset.loadMode || 
                           grid.getAttribute('data-load-mode') || 
                           'infinite';
      this.#loadMode = loadModeValue.trim();
      this.#totalArticles = Number(
        grid.dataset.totalArticles || 
        grid.getAttribute('data-total-articles') || 
        0
      );
    }

    if (this.#loadMode === 'button') {
      if (this.infinityScrollObserver) {
        this.infinityScrollObserver.disconnect();
        const { viewMorePrevious, viewMoreNext } = this.refs;
        if (viewMorePrevious instanceof HTMLElement) {
          this.infinityScrollObserver.unobserve(viewMorePrevious);
        }
        if (viewMoreNext instanceof HTMLElement) {
          this.infinityScrollObserver.unobserve(viewMoreNext);
        }
        this.infinityScrollObserver = undefined;
      }

      const { viewMorePrevious, viewMoreNext } = this.refs;
      if (viewMorePrevious instanceof HTMLElement) {
        viewMorePrevious.style.display = 'none';
        viewMorePrevious.hidden = true;
      }
      if (viewMoreNext instanceof HTMLElement) {
        viewMoreNext.style.display = 'none';
        viewMoreNext.hidden = true;
      }

      requestAnimationFrame(() => {
        this.#setupLoadButtonMode();
      });
    }
  }

  #setupLoadButtonMode() {
    const { loadMoreContainer, loadMoreButton, progressText, progressBarFill } = this.refs;
    
    this.#loadMoreContainer = loadMoreContainer instanceof HTMLElement ? loadMoreContainer : null;
    this.#loadMoreButton = loadMoreButton instanceof HTMLButtonElement ? loadMoreButton : null;
    this.#progressText = progressText instanceof HTMLElement ? progressText : null;
    this.#progressBarFill = progressBarFill instanceof HTMLElement ? progressBarFill : null;

    if (!this.#loadMoreButton) {
      const button = this.querySelector('[ref="loadMoreButton"]');
      if (button instanceof HTMLButtonElement) {
        this.#loadMoreButton = button;
      } else {
        return;
      }
    }

    if (!this.#loadMoreContainer) {
      const container = this.querySelector('[ref="loadMoreContainer"]');
      if (container instanceof HTMLElement) {
        this.#loadMoreContainer = container;
      }
    }

    if (!this.#progressText) {
      const text = this.querySelector('[ref="progressText"]');
      if (text instanceof HTMLElement) {
        this.#progressText = text;
      }
    }

    if (!this.#progressBarFill) {
      const fill = this.querySelector('[ref="progressBarFill"]');
      if (fill instanceof HTMLElement) {
        this.#progressBarFill = fill;
      }
    }

    if (this.#loadMoreContainer) {
      this.#loadMoreContainer.hidden = false;
      this.#loadMoreContainer.style.display = '';
    }

    this.#loadMoreButton.removeEventListener('click', this.#handleLoadMoreClick);
    this.#loadMoreButton.addEventListener('click', this.#handleLoadMoreClick);
    const { shownCount, totalCount } = this.#updateProgress();
    if (shownCount >= totalCount) {
      this.#hideLoadMore();
      return;
    }
    this.#fetchPage('next');
  }

  #handleLoadMoreClick = async () => {
    if (!this.#loadMoreButton) return;

    this.#loadMoreButton.disabled = true;
    const originalText = this.#loadMoreButton.textContent;
    if (originalText) {
      this.#loadMoreButton.textContent = 'Loading...';
    }

    try {
      await this.#renderNextPageForButton();
    } finally {
      if (this.#loadMoreButton) {
        this.#loadMoreButton.disabled = false;
        if (originalText) {
          this.#loadMoreButton.textContent = originalText;
        }
      }
    }
  };

  async #renderNextPageForButton() {
    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#hideLoadMore();
      return;
    }

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) {
        this.#hideLoadMore();
        return;
      }
    }

    const pageHTML = this.pages.get(nextPage.page);
    if (pageHTML) {
      const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
      const newPageGrid = parsedPage.querySelector('[ref="grid"]');
      const columnsNew = newPageGrid?.querySelector('.blog-posts-columns');
      if (columnsNew) {
        const elementsFromColumns = Array.from(columnsNew.querySelectorAll('[ref="cards[]"]'))
          .filter(el => el instanceof HTMLElement);
        this.#addElementsToAllColumns(elementsFromColumns);
      } else {
        const elements = Array.from(nextPageItemElements)
          .filter(el => el instanceof HTMLElement);
        this.#addElementsToAllColumns(elements);
      }
    } else {
      const elements = Array.from(nextPageItemElements)
        .filter(el => el instanceof HTMLElement);
      this.#addElementsToAllColumns(elements);
    }

    this.updateHistoryState(nextPage.url);
    const { shownCount, totalCount } = this.#updateProgress();

    if (shownCount >= totalCount || !this.#hasMorePages()) {
      this.#hideLoadMore();
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  #updateProgress() {
    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) {
      return { shownCount: 0, totalCount: this.#totalArticles };
    }

    const cards = this.#getCardElements();
    const shownCount = cards.length;
    const totalCount = this.#totalArticles;
    const percentage = totalCount > 0 ? (shownCount / totalCount) * 100 : 0;

    if (this.#progressText) {
      this.#progressText.textContent = `You've viewed ${shownCount} of ${totalCount} articles`;
    }

    if (this.#progressBarFill) {
      this.#progressBarFill.style.width = `${Math.min(percentage, 100)}%`;
    }

    return { shownCount, totalCount };
  }

  #hasMorePages() {
    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) return false;

    const lastPage = Number(grid.dataset.lastPage) || 0;
    const cards = this.#getCardElements();
    
    if (cards.length === 0) return false;

    const lastCard = cards[cards.length - 1];
    if (!lastCard || !(lastCard instanceof HTMLElement)) return false;
    const currentPage = Number(lastCard.dataset.page) || 1;

    return currentPage < lastPage;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = this.#getCardElements();
    const isPrevious = type === 'previous';

    if (!cards.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard || !(targetCard instanceof HTMLElement)) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * @param {{ page: number, url: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) return false;
    const lastPage = grid.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  #getGridForPage(page) {
    // @ts-ignore
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll('[ref="cards[]"]');
  }

  #getVisibleColumns() {
    // @ts-ignore
    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) return [];
    
    const columns = grid.querySelector('.blog-posts-columns');
    if (!columns) return [];
    
    const allCols = Array.from(columns.querySelectorAll('.blog-posts-column'));
    return allCols.filter(col => {
      if (!(col instanceof HTMLElement)) return false;
      const style = window.getComputedStyle(col);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  #redistributeHiddenColumnElements() {
    const visibleCols = this.#getVisibleColumns();
    if (visibleCols.length === 0) return;

    // @ts-ignore
    const { grid } = this.refs;
    const columns = grid?.querySelector('.blog-posts-columns');
    if (!columns) return;

    const allCols = Array.from(columns.querySelectorAll('.blog-posts-column'));
    const hiddenCols = allCols.filter(col => !visibleCols.includes(col));
    if (hiddenCols.length === 0) return;

    /** @type {HTMLElement[]} */
    const hiddenElements = [];
    hiddenCols.forEach(col => {
      Array.from(col.querySelectorAll('[ref="cards[]"]')).forEach(card => {
        if (card instanceof HTMLElement) hiddenElements.push(card);
      });
    });

    if (hiddenElements.length === 0) return;

    const totalExisting = visibleCols.reduce((sum, col) => 
      sum + col.querySelectorAll('[ref="cards[]"]').length, 0
    );

    hiddenElements.forEach((element, index) => {
      const targetCol = visibleCols[(totalExisting + index) % visibleCols.length];
      targetCol?.appendChild(element);
    });
  }

  #addElementsToAllColumns(elementsArray) {
    const visibleCols = this.#getVisibleColumns();
    if (visibleCols.length === 0) return;

    const totalExisting = visibleCols.reduce((sum, col) => 
      sum + col.querySelectorAll('[ref="cards[]"]').length, 0
    );

    elementsArray.forEach((element, index) => {
      const cloned = element.cloneNode(true);
      if (!(cloned instanceof HTMLElement)) return;
      
      const targetCol = visibleCols[(totalExisting + index) % visibleCols.length];
      targetCol?.appendChild(cloned);
    });

    requestIdleCallback(() => {
      this.#redistributeHiddenColumnElements();
    });
  }


  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      }
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    
    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    }
  }

  /**
   * @param {number} pageNumber
   * @param {URL} url
   */
  async #fetchSpecificPage(pageNumber, url) {
    const pageInfo = { page: pageNumber, url };

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  #handleFilterUpdate = () => {
    const { grid } = this.refs;
    if (!grid) return;
    
    const currentLastPage = grid.dataset.lastPage;
    
    const observer = new MutationObserver(() => {
      const newLastPage = grid.dataset.lastPage;
      
      if (newLastPage !== currentLastPage) {
        observer.disconnect();
        
        if (!this.isConnected) return;
        
        const loadModeFromDOM = grid.dataset.loadMode || 
                                grid.getAttribute('data-load-mode') || 
                                'infinite';
        const shouldBeButtonMode = loadModeFromDOM.trim() === 'button';
        
        this.#initializeLoadMode();
      }
    });
    
    observer.observe(grid, {
      attributes: true,
      attributeFilter: ['data-last-page', 'data-load-mode'],
      childList: true,
    });
    
    setTimeout(() => {
      observer.disconnect();
      if (this.isConnected) {
        this.#initializeLoadMode();
      }
    }, 3000);
  };

  #setupCategoryFilterTabs() {
    this.addEventListener('click', this.#handleCategoryFilterClick);
  }

  #handleCategoryFilterClick = async (event) => {
    // @ts-ignore
    const target = event.target;
    if (!(target instanceof Element)) return;
    
    const link = target.closest('[data-category-filter]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute('href');
    if (!href) return;

    event.preventDefault();

    const url = new URL(href, window.location.origin);
    window.history.pushState({}, '', url.toString());
    document.dispatchEvent(new FilterUpdateEvent(url.searchParams));
    await sectionRenderer.renderSection(this.sectionId, { cache: false, url });
  };

  disconnectedCallback() {
    if (this.#loadMoreButton) {
      this.#loadMoreButton.removeEventListener('click', this.#handleLoadMoreClick);
    }
    
    this.removeEventListener('click', this.#handleCategoryFilterClick);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    
    super.disconnectedCallback();
  }

  #getCardElements() {
    const { grid } = this.refs;
    if (!grid || !(grid instanceof HTMLElement)) return [];

    const columns = grid.querySelector('.blog-posts-columns');
    if (!columns) return [];

    const allCards = Array.from(columns.querySelectorAll('[ref="cards[]"]'));
    
    return allCards.filter(card => {
      if (!(card instanceof HTMLElement)) return false;
      const style = window.getComputedStyle(card);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
  }

  #hideLoadMore() {
    if (!this.#loadMoreContainer) return;
    this.#loadMoreContainer.hidden = true;
    this.#loadMoreContainer.style.display = 'none';
  }

  updateHistoryState(url) {
    // @ts-ignore
    if (!(url instanceof URL)) return;
    const sanitizedUrl = new URL(url.toString());
    sanitizedUrl.searchParams.delete('page');
    history.pushState('', '', sanitizedUrl.toString());
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
